// src/components/ProjectsPanel.tsx
import React, { useState, useEffect } from "react";
import { theme } from "@/styles/theme";
import type { ProjectDef, ProjectItem, ProjectFolder } from "@/projectsConfig";
import { isFolder } from "@/projectsConfig";
import { ScrollArea } from "@/components/ScrollArea";

type ProjectsPanelProps = {
  projects: ProjectItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;

  /**
   * Called when the user presses the CTA button for a project.
   * The caller (Home) decides whether to:
   *  - open an external link in a new tab,
   *  - navigate internally,
   *  - or show a password prompt.
   */
  onRequestPlay: (project: ProjectDef) => void;

  /**
   * Called when the user clicks the play icon on a project's header image.
   * Opens the video player window with the project's video.
   */
  onRequestPlayVideo: (project: ProjectDef) => void;

  /**
   * Called when the user clicks a folder tile.
   * Opens a new window with the folder's projects.
   */
  onOpenFolder?: (folder: ProjectFolder, anchorRect: DOMRect) => void;
};

// Local hook to detect mobile
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [breakpoint]);

  return isMobile;
}

export function ProjectsPanel({
  projects,
  selectedId,
  onSelect,
  onRequestPlay,
  onRequestPlayVideo,
  onOpenFolder,
}: ProjectsPanelProps) {
  const isMobile = useIsMobile();

  // Only non-folder items can be selected for the detail panel
  const projectDefs = projects.filter((p): p is ProjectDef => !isFolder(p));
  const selectedProject =
    projectDefs.find((p) => p.id === selectedId) ?? projectDefs[0] ?? null;

  // Hover / pressed tracking for icon tiles
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [pressedId, setPressedId] = useState<string | null>(null);

  // Hover / pressed tracking for play icon overlay
  const [playIconHovered, setPlayIconHovered] = useState(false);
  const [playIconPressed, setPlayIconPressed] = useState(false);

  // Left grid layout differs for mobile vs desktop
  const tilesContainerStyle: React.CSSProperties = isMobile
    ? {
        display: "flex",
        flexDirection: "column",
        gap: 12,
        alignItems: "stretch",
      }
    : {
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "flex-start",
      };

  return (
    <div
      style={{
        background: theme.colors.DarkGray,
        border: `1px solid ${theme.colors.White}`,
        boxShadow: `2px 2px 0 ${theme.colors.DarkGray}`,
        padding: 0,
        height: "100%", // fixed by window body
        minHeight: 260,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          height: "100%",
        }}
      >
        {/* LEFT COLUMN: icons + custom Win95 scrollbar */}
        <div
          style={{
            flex: isMobile ? "0 0 40%" : 1,
            maxWidth: isMobile ? "40%" : "none",
            borderRight: `1px solid ${theme.colors.White}`,
            boxSizing: "border-box",
            height: "100%",
          }}
        >
          <ScrollArea
            style={{
              width: "100%",
              height: "100%",
            }}
            contentStyle={{
              paddingTop: 16,
              paddingLeft: 16,
              paddingRight: 16,
              paddingBottom: 8,
              boxSizing: "border-box",
            }}
          >
            <div style={tilesContainerStyle}>
              {projects.map((item) => {
                const itemIsFolder = isFolder(item);
                const isSelected =
                  !itemIsFolder &&
                  (selectedId ?? projectDefs[0]?.id) === item.id;
                const isHovered = hoveredId === item.id;
                const isPressed = pressedId === item.id;

                let borderColor = "transparent";
                let tileBackground: string = "transparent";

                if (isSelected) {
                  borderColor = theme.colors.Green;
                  tileBackground = "rgba(123, 255, 142, 0.10)";
                } else if (isPressed) {
                  tileBackground = "rgba(223, 223, 223, 0.20)";
                } else if (isHovered) {
                  tileBackground = "rgba(255, 255, 255, 0.20)";
                }

                const iconBoxStyle: React.CSSProperties = {
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  width: 67.5,
                  height: 67.5,
                  background: "#000000",
                  borderTop: "1px solid #C0C0C0",
                  borderLeft: "1px solid #C0C0C0",
                  borderRight: "1px solid #808080",
                  borderBottom: "1px solid #808080",
                };

                const outerIconBoxStyle: React.CSSProperties = {
                  display: "inline-flex",
                  boxSizing: "border-box",
                  borderTop: "1px solid #808080",
                  borderLeft: "1px solid #808080",
                  borderRight: "1px solid #000000",
                  borderBottom: "1px solid #000000",
                };

                const textStyle: React.CSSProperties = {
                  color: theme.colors.White,
                  textAlign: "center",
                  fontFamily: theme.fonts.body,
                  fontSize: 14,
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: 1.2,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                };

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={(e) => {
                      if (itemIsFolder) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        onOpenFolder?.(item as ProjectFolder, rect);
                      } else {
                        onSelect(item.id);
                      }
                    }}
                    onMouseEnter={() => setHoveredId(item.id)}
                    onMouseLeave={() => {
                      setHoveredId(null);
                      setPressedId(null);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setPressedId(item.id);
                    }}
                    onMouseUp={(e) => {
                      e.preventDefault();
                      setPressedId(null);
                    }}
                    style={{
                      display: "inline-flex",
                      width: isMobile ? "100%" : 80,
                      padding: 4,
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 12,
                      background: tileBackground,
                      border: `1px solid ${borderColor}`,
                      cursor: "pointer",
                      fontFamily: theme.fonts.body,
                      boxSizing: "border-box",
                    }}
                  >
                    {/* Icon box */}
                    {itemIsFolder ? (
                      <img
                        src={item.iconSrc}
                        alt={item.name}
                        style={{
                          width: 67.5,
                          height: 67.5,
                          display: "block",
                          imageRendering: "pixelated",
                        }}
                      />
                    ) : (
                      <div style={outerIconBoxStyle}>
                        <div style={iconBoxStyle}>
                          <img
                            src={item.iconSrc}
                            alt={item.name}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              imageRendering: "pixelated",
                              display: "block",
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Label */}
                    <span style={textStyle}>{item.name}</span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* RIGHT COLUMN: details */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            height: "100%",
          }}
        >
          {selectedProject ? (
            <>
              {/* Header image with optional play icon overlay */}
              <div
                style={{
                  width: "100%",
                  flexShrink: 0,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <img
                  src={selectedProject.headerSrc}
                  alt={selectedProject.name}
                  style={{
                    width: "100%",
                    height: 140,
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                {/* Play icon overlay - only shown when videoSrc exists */}
                {selectedProject.videoSrc && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestPlayVideo(selectedProject);
                    }}
                    onMouseEnter={() => setPlayIconHovered(true)}
                    onMouseLeave={() => {
                      setPlayIconHovered(false);
                      setPlayIconPressed(false);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPlayIconPressed(true);
                    }}
                    onMouseUp={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPlayIconPressed(false);
                    }}
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: `translate(-50%, -50%) scale(${
                        playIconPressed ? 0.95 : playIconHovered ? 1.1 : 1
                      })`,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      filter: playIconHovered
                        ? `drop-shadow(0 0 8px ${theme.colors.Green})`
                        : "none",
                      pointerEvents: "auto",
                    }}
                  >
                    <img
                      src="/images/Icon-Play.svg"
                      alt="Play video"
                      style={{
                        width: 70,
                        height: 70,
                        display: "block",
                        imageRendering: "pixelated",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Content block */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: "12px 20px 12px 20px",
                  boxSizing: "border-box",
                  color: theme.colors.White,
                }}
              >
                {/* Text group with extra internal padding */}
                <div
                  style={{
                    padding: "16px 0px 0px 0px",
                  }}
                >
                  {/* Name */}
                  <div
                    style={{
                      fontFamily: theme.fonts.body,
                      fontSize: 22,
                      fontWeight: 700,
                      marginBottom: 2,
                    }}
                  >
                    {selectedProject.name}
                  </div>

                  {/* Platform */}
                  <div
                    style={{
                      fontFamily: theme.fonts.body,
                      fontSize: 16,
                      color: theme.colors.Green,
                      marginBottom: 10,
                    }}
                  >
                    {selectedProject.platform}
                  </div>

                  {/* Description */}
                  <div
                    style={{
                      fontFamily: theme.fonts.body,
                      fontSize: 15,
                      lineHeight: 1.5,
                      color: theme.colors.White,
                    }}
                  >
                    {selectedProject.description}
                  </div>
                </div>

                {/* CTA anchored to bottom - only show if href exists */}
                {selectedProject.href && (
                  <div
                    style={{
                      marginTop: "auto",
                      paddingBottom: 6,
                    }}
                  >
                    <ProjectCtaButton
                      project={selectedProject}
                      onRequestPlay={onRequestPlay}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: theme.colors.TextMuted,
                padding: 16,
                textAlign: "center",
              }}
            >
              No projects defined yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// CTA button now only delegates to onRequestPlay
type CtaProps = {
  project: ProjectDef;
  onRequestPlay: (project: ProjectDef) => void;
};

function ProjectCtaButton({ project, onRequestPlay }: CtaProps) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const disabled = !project.href;

  let border = `1px solid ${theme.colors.White}`;
  let background = theme.colors.Dark;
  let color = theme.colors.White;
  let boxShadow =
    `-3px -3px 0 0 rgba(127, 127, 127, 0.50) inset, 2px 2px 0 0 ${theme.colors.LightGray} inset`;
  let cursor: React.CSSProperties["cursor"] = disabled ? "default" : "pointer";

  if (disabled) {
    border = `1px solid ${theme.colors.MidGray}`;
    background = theme.colors.DarkAlt;
    color = theme.colors.Gray;
    boxShadow =
      `-3px -3px 0 0 rgba(127, 127, 127, 0.50) inset, 2px 2px 0 0 ${theme.colors.MidGray} inset`;
  } else if (pressed) {
    border = `1px solid ${theme.colors.White}`;
    background = "#363636";
    color = theme.colors.White;
    boxShadow =
      "-3px -3px 0 0 rgba(150, 150, 150, 0.50) inset, 2px 2px 0 0 rgba(0, 0, 0, 0.50) inset";
  } else if (hovered) {
    border = `2px solid ${theme.colors.Green}`;
    background = theme.colors.Dark;
    color = theme.colors.Green;
    boxShadow = "none";
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onRequestPlay(project);
      }}
      onMouseEnter={() => {
        if (!disabled) setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={(e) => {
        if (disabled) return;
        e.preventDefault();
        setPressed(true);
      }}
      onMouseUp={(e) => {
        if (disabled) return;
        e.preventDefault();
        setPressed(false);
      }}
      style={{
        width: "100%",
        height: 40,
        padding: "8px 10px",
        background,
        border,
        color,
        boxShadow,
        fontFamily: theme.fonts.body,
        fontSize: 16,
        cursor,
        outline: "none",
        boxSizing: "border-box",
      }}
    >
      {project.ctaLabel}
    </button>
  );
}
